import React from "react";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useLazyListCrossDockingQuery } from "store/services/crossDockingApi";

import { useAppMeta, useTablePagination } from "hooks";
import HighlightedText from "components/Shared/HighlightedText";

const CrossDockingListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Cross Docking" }];

  const crossDockingTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];
  const columns = [
    {
      name: "Cross Docking ID",
      selector: (row) => row?.crossDockId,
      cell: (row) => {
        const displayId = row?.crossDockId;
        const path = `/app/crm/cross-docking/${row?._id}/detail`;

        return displayId ? (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            <HighlightedText text={displayId} highlight={keyword} />
          </Link>
        ) : (
          "N/A"
        );
      },
    },

    {
      name: "Source",
      selector: (row) => row?.sourceBranch?.name,
      cell: (row) => row?.sourceBranch?.name,
    },
    {
      name: "Destination",
      selector: (row) => row?.destinationBranch?.name,
      cell: (row) => row?.destinationBranch?.name,
    },
    {
      name: "Shipment ID",
      selector: (row) => row?.shipment?.shipmentId,
      cell: (row) => {
        const shiId = row?.shipment?.shipmentId
          ?.replace(/(.{4})/g, "$1-")
          .slice(0, -1);
        const displayId = `${shiId}${row.IsReturning ? " (R)" : ""}`;
        const path = `/app/crm/shipment/${row?.shipment?._id}/detail`;
        return (
          <Link
            title={displayId}
            to={path}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {displayId ? (
              <HighlightedText text={displayId} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        );
      },
    },
    {
      name: "Loading Time",
      selector: (row) => <AppTableDateTime date={row?.transferTime} />,
    },

    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => row?.status,
    },
    {
      name: "Created By",
      selector: (row) => row.createdBy?.name || "N/A",
      cell: (row) => row.createdBy?.name || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/cross-docking/${row?._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Cross Docking"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/cross-docking/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Cross Docking">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchCrossDocking,
    { data: crossDockingRes, isLoading, isFetching, error },
  ] = useLazyListCrossDockingQuery();

  React.useEffect(() => {
    fetchCrossDocking({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchCrossDocking, keyword, perPage]);

  useAppMeta(`${activeTab} Cross Docking`);
  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={crossDockingTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Cross Docking ID"
          disabled={isLoading || isFetching}
        ></TableFilter>

        <AppTable
          columns={columns}
          data={crossDockingRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={crossDockingRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default CrossDockingListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/cross-docking/create">
        <Button startIcon={<Add />} variant="contained" color="primary">
          Add Cross Docking
        </Button>
      </Link>
    </>
  );
};
